/**
 * User Management – API wiring using existing api client.
 * Placeholders for endpoints not yet available (stats, loyalty, activity, notes).
 */
import api from './api';
import { formatOrderRef } from '../utils/orderNumberFormat';

export interface UserListItem {
  id: number;
  name: string;
  email: string;
  phone?: string | null;
  address?: string | null;
  role: string;
  is_active: boolean;
  is_shadowbanned?: boolean;
  created_at: string;
}

export interface UsersListResponse {
  success: boolean;
  data: UserListItem[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface UserDetailResponse {
  success: boolean;
  data: UserListItem;
}

export interface DashboardStatsData {
  totalUsers?: number;
  newUsers?: number;
  userGrowth?: number;
}

export interface UserListStats {
  totalUsers: number;
  activeToday: number;
  loyaltyPointsIssued: number;
  newSignups: number;
}

/** List users with pagination and search (existing GET /users) */
export async function fetchUsers(params: {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  shadowbanned?: boolean;
}): Promise<UsersListResponse> {
  const { page = 1, limit = 10, search = '', shadowbanned } = params;
  const res = await api.get<UsersListResponse>('/users', {
    params: {
      page,
      limit,
      search,
      ...(shadowbanned ? { shadowbanned: 'true' } : {}),
    },
  });
  return res.data;
}

/** Get single user (existing GET /users/:id) */
export async function fetchUserById(id: string): Promise<UserDetailResponse> {
  const res = await api.get<UserDetailResponse>(`/users/${id}`);
  return res.data;
}

/** Create user (POST /users) - legacy */
export interface CreateUserPayload {
  name: string;
  email: string;
  password?: string;
  phone?: string;
  address?: string;
  role?: string;
}
export async function createUser(
  payload: CreateUserPayload
): Promise<{ success: boolean; data?: UserListItem; message?: string }> {
  const { data } = await api.post<{
    success: boolean;
    data?: UserListItem;
    message?: string;
  }>('/users', payload);
  return data ?? { success: false, message: 'Unknown error' };
}

/** Create user via POST /api/admin/users - phone required, email optional, role always CUSTOMER */
export interface CreateAdminUserPayload {
  name: string;
  phone: string;
  password: string;
  email?: string;
  address?: string;
}
export async function createAdminUser(
  payload: CreateAdminUserPayload
): Promise<{ success: boolean; data?: UserListItem; message?: string }> {
  const res = await api.post<{
    success: boolean;
    data?: UserListItem;
    message?: string;
    code?: string;
  }>('/admin/users', {
    ...payload,
    role: 'customer',
  });
  return res.data ?? { success: false, message: 'Unknown error' };
}

/** Update user profile via PATCH /api/admin/users/:id (backend uses PUT) */
export interface UpdateUserProfilePayload {
  name?: string;
  phone?: string;
  email?: string;
  address?: string;
  gender?: string;
  dob?: string;
}
export async function updateUserProfile(
  userId: string,
  payload: UpdateUserProfilePayload
): Promise<{ success: boolean; data?: UserListItem; message?: string }> {
  const { gender, dob, ...rest } = payload;
  const body: Record<string, unknown> = { ...rest };
  if (gender != null) body.gender = gender;
  if (dob != null) body.dob = dob;
  const res = await api.put<{
    success: boolean;
    data?: UserListItem;
    message?: string;
  }>(`/admin/users/${userId}`, body);
  return res.data ?? { success: false, message: 'Unknown error' };
}

/** Update user status via PATCH /api/admin/users/:id/status or PUT with isActive */
export async function updateUserStatus(
  userId: string,
  status: 'ACTIVE' | 'INACTIVE'
): Promise<{ success: boolean; data?: UserListItem; message?: string }> {
  const isActive = status === 'ACTIVE';
  const res = await api.put<{
    success: boolean;
    data?: UserListItem;
    message?: string;
  }>(`/admin/users/${userId}`, { isActive, clearRestrictions: isActive });
  return res.data ?? { success: false, message: 'Unknown error' };
}

/** Dashboard stats – reuse for Total Users / New Sign-ups; placeholder for Active Today / Loyalty */
export async function fetchUserListStats(): Promise<UserListStats> {
  try {
    const [statsRes, usersRes] = await Promise.all([
      api.get<{ success: boolean; data?: DashboardStatsData }>(
        '/dashboard/stats'
      ),
      api.get<UsersListResponse>('/users', { params: { limit: 1 } }),
    ]);
    const totalUsers =
      statsRes.data?.data?.totalUsers ?? usersRes.data?.pagination?.total ?? 0;
    const newSignups = statsRes.data?.data?.newUsers ?? 0;
    return {
      totalUsers,
      activeToday: 0, // placeholder – no endpoint
      loyaltyPointsIssued: 0, // placeholder – no endpoint
      newSignups,
    };
  } catch {
    return {
      totalUsers: 0,
      activeToday: 0,
      loyaltyPointsIssued: 0,
      newSignups: 0,
    };
  }
}

/** Order stats for user (success rate, avg spending, last order – from DB) */
export interface UserOrderStats {
  totalOrders: number;
  completedOrders: number;
  successRate: number | null;
  avgSpending: number | null;
  lastOrderAt: string | null;
}
export async function fetchUserOrderStats(
  userId: string
): Promise<{ success: boolean; data: UserOrderStats }> {
  const res = await api.get(`/users/${userId}/order-stats`);
  return res.data;
}

/** Orders for user – use userId when available for accurate filtering */
export async function fetchOrdersForUser(params: {
  page?: number;
  limit?: number;
  search?: string;
  userId?: string;
}): Promise<{
  success: boolean;
  data: OrderRecord[];
  pagination?: { page: number; limit: number; total: number; pages: number };
}> {
  const { page = 1, limit = 10, search = '', userId } = params;
  const res = await api.get('/orders', {
    params: { page, limit, search, ...(userId ? { user_id: userId } : {}) },
  });
  return res.data;
}

export interface OrderRecord {
  id: number;
  order_number?: string;
  total: number;
  status: string;
  created_at: string;
  customerName?: string;
  customer_phone?: string;
  restaurantName?: string;
}

export interface UserOverviewResponse {
  success: boolean;
  data: {
    profile: {
      id: number;
      code: string;
      name: string;
      email: string;
      phone: string | null;
      status: string;
      isShadowbanned?: boolean;
      codRestricted?: boolean;
      codRestrictedUntil?: string | null;
      codRestrictionReason?: string | null;
      accountRestricted?: boolean;
      accountRestrictedUntil?: string | null;
      joinedAt: string;
      gender: string | null;
      dob: string | null;
      address: string | null;
    };
    stats: {
      totalOrders: number;
      totalSpent: number;
      favoriteCategory: string | null;
      favoriteMerchant: string | null;
    };
    loyalty: {
      tier: string;
      currentPoints: number;
      lifetimeEarned: number;
      expiringSoon: number;
      nextTier: string | null;
      nextTierGoal: number | null;
    };
    vouchers: Array<{
      title: string;
      code: string;
      expiresAt: string;
      status: string;
    }>;
  };
}

/** PATCH /admin/users/:userId/shadowban */
export async function updateUserShadowban(
  userId: string,
  isShadowbanned: boolean
): Promise<{ success: boolean; message?: string }> {
  const res = await api.patch<{ success: boolean; message?: string }>(
    `/admin/users/${userId}/shadowban`,
    { isShadowbanned }
  );
  return res.data ?? { success: false };
}

/** POST /admin/users/:userId/unlock-cod */
export async function unlockUserCod(
  userId: string
): Promise<{ success: boolean; message?: string }> {
  const res = await api.post<{ success: boolean; message?: string }>(
    `/admin/users/${userId}/unlock-cod`
  );
  return res.data ?? { success: false };
}

/** GET /api/admin/users/:userId/overview */
export async function fetchUserOverview(
  userId: string
): Promise<UserOverviewResponse> {
  const res = await api.get<UserOverviewResponse>(
    `/admin/users/${userId}/overview`
  );
  return res.data;
}

export interface UserOrdersResponse {
  success: boolean;
  data: Array<{
    id: number;
    orderCode?: string;
    order_number?: string;
    createdAt?: string;
    created_at?: string;
    status: string;
    paymentMethod?: string | null;
    totalAmount?: number;
    total?: number;
    merchantName?: string | null;
    restaurantName?: string | null;
    restaurant_image?: string | null;
    restaurantImage?: string | null;
  }>;
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

/** GET /api/admin/users/:userId/orders with pagination */
export async function fetchUserOrders(
  userId: string,
  params: { page?: number; limit?: number; search?: string }
): Promise<UserOrdersResponse> {
  const res = await api.get<UserOrdersResponse>(
    `/admin/users/${userId}/orders`,
    { params }
  );
  return res.data;
}

/** Loyalty transaction from API */
export interface LoyaltyTransaction {
  id: string;
  date: string;
  activity: string;
  change: number;
  balance: number;
}

export interface UserLoyaltyTransactionsResponse {
  success: boolean;
  data: LoyaltyTransaction[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

/** GET /api/admin/users/:userId/loyalty/transactions */
export async function fetchUserLoyaltyTransactions(
  userId: string,
  params?: { page?: number; limit?: number }
): Promise<UserLoyaltyTransactionsResponse> {
  const res = await api.get<UserLoyaltyTransactionsResponse>(
    `/admin/users/${userId}/loyalty/transactions`,
    { params: params ?? {} }
  );
  return res.data;
}

export interface UserActivityEntry {
  action: string;
  actor: string;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface UserActivityResponse {
  success: boolean;
  data: UserActivityEntry[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

/** GET /api/admin/users/:userId/activity */
export async function fetchUserActivity(
  userId: string,
  params?: { page?: number; limit?: number; search?: string }
): Promise<UserActivityResponse> {
  const res = await api.get<UserActivityResponse>(
    `/admin/users/${userId}/activity`,
    { params: params ?? {} }
  );
  return res.data;
}

/** Format activity entry for display (from new /activity API shape) */
export function formatActivityEntry(entry: UserActivityEntry): {
  title: string;
  description: string;
} {
  const m = entry.metadata || {};
  switch (entry.action) {
    case 'ORDER_PLACED': {
      const orderRef = m.orderNumber
        ? `Order #${m.orderNumber}`
        : `Order #${formatOrderRef(
            (m.orderId as number) ?? '',
            (m.orderCreatedAt as string) || entry.createdAt
          )}`;
      const restName = m.restaurantName ? ` at ${m.restaurantName}` : '';
      return {
        title: 'Order Placed',
        description: `${orderRef}${restName}. Total: ${formatVnd(
          (m.total as number) ?? 0
        )}`,
      };
    }
    case 'REVIEW_SUBMITTED': {
      const orderRef = m.orderId
        ? ` for Order #${
            m.orderNumber ??
            formatOrderRef(
              m.orderId as number,
              (m.orderCreatedAt as string) || entry.createdAt
            )
          }`
        : '';
      const foodName = m.foodName ?? 'item';
      return {
        title: 'Submitted a Review',
        description: `Left a ${
          m.rating ?? 0
        }-star rating for "${foodName}"${orderRef}.`,
      };
    }
    case 'FAVORITE_ADDED':
      return {
        title: 'Added to Favorites',
        description: `Added "${
          (m.foodName as string) ?? 'item'
        }" to favorites.`,
      };
    default:
      return { title: entry.action, description: '' };
  }
}

/** Format amount as VND */
export function formatVnd(amount: number): string {
  return new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: 'VND',
    maximumFractionDigits: 0,
  }).format(amount);
}

/** Placeholder: save note (no backend – persist in memory/localStorage per user) */
const notesStore: Record<string, string> = {};
export function getStoredNote(userId: string): string {
  if (typeof window === 'undefined') return '';
  return (
    localStorage.getItem(`user-note-${userId}`) ?? notesStore[userId] ?? ''
  );
}
export function saveNote(userId: string, text: string): void {
  notesStore[userId] = text;
  if (typeof window !== 'undefined') {
    localStorage.setItem(`user-note-${userId}`, text);
  }
}

/** Admin đặt mật khẩu mới cho user (customer). */
export async function resetUserPassword(
  userId: string,
  newPassword: string
): Promise<void> {
  const response = await api.post(`/users/${userId}/reset-password`, {
    newPassword,
  });
  if (!response.data?.success) {
    throw new Error(response.data?.message || 'Failed to reset password');
  }
}
